import React from 'react';
import { motion } from 'framer-motion';
import { 
  EyeIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const RequestList = ({ requests, onView, getStatusColor, getPriorityColor, user }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      it: '💻',
      hr: '👥',
      admin: '📋',
      facilities: '🏢',
      other: '📝'
    };
    return icons[category] || icons.other;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      it: 'IT Support',
      hr: 'HR Services',
      admin: 'Administrative',
      facilities: 'Facilities',
      other: 'Other'
    };
    return labels[category] || category;
  };

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <ClipboardDocumentListIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
        <p className="text-gray-500">No requests match your current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Requests ({requests.length})</h3>
      </div>
      
      <div className="divide-y divide-gray-200">
        {requests.map((request, index) => (
          <motion.div
            key={request.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                {/* Category Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">{getCategoryIcon(request.category)}</span>
                  </div>
                </div>

                {/* Request Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {request.title}
                    </h4>
                    {request.isUrgent && (
                      <ExclamationTriangleIcon className="w-4 h-4 text-red-600" title="Urgent Request" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {request.description}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getCategoryLabel(request.category)}
                    </span>
                    {request.subcategory && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {request.subcategory}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {request.status.replace('_', ' ')}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request.priority)}`}>
                      {request.priority}
                    </span>
                    <span className="flex items-center">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {formatDate(request.createdAt)}
                    </span>
                    <span className="flex items-center">
                      <UserIcon className="w-3 h-3 mr-1" />
                      {request.Employee?.firstName} {request.Employee?.lastName}
                    </span>
                    {request.assignedTo && (
                      <span className="flex items-center">
                        <UserIcon className="w-3 h-3 mr-1" />
                        Assigned to {request.AssignedTo?.firstName} {request.AssignedTo?.lastName}
                      </span>
                    )}
                    {request.dueDate && (
                      <span className="flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        Due {formatDate(request.dueDate)}
                      </span>
                    )}
                  </div>

                  {/* Workflow Progress */}
                  {request.currentStage && request.totalStages && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Progress:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(request.currentStage / request.totalStages) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          Stage {request.currentStage} of {request.totalStages}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Cost Information */}
                  {request.estimatedCost && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Estimated Cost:</span> ${request.estimatedCost}
                      {request.actualCost && (
                        <span className="ml-2">
                          <span className="font-medium">Actual Cost:</span> ${request.actualCost}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onView(request)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                  title="View Request"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Status Timeline */}
            {request.RequestWorkflows && request.RequestWorkflows.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>Workflow:</span>
                  {request.RequestWorkflows.map((workflow, wIndex) => (
                    <div key={workflow.id} className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${
                        workflow.status === 'approved' ? 'bg-green-500' :
                        workflow.status === 'rejected' ? 'bg-red-500' :
                        workflow.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-300'
                      }`}></div>
                      <span className="text-xs">
                        {workflow.stageName}
                        {workflow.status !== 'pending' && (
                          <span className={`ml-1 ${
                            workflow.status === 'approved' ? 'text-green-600' :
                            workflow.status === 'rejected' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            ({workflow.status})
                          </span>
                        )}
                      </span>
                      {wIndex < request.RequestWorkflows.length - 1 && (
                        <span className="text-gray-300">→</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default RequestList; 